import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  CouponDashboardError,
  EunpyeongCareCoupon,
  fetchEunpyeongCareCoupon,
  localizedCouponText,
} from '../../services/eunpyeongCareDashboard.service';
import './eunpyeongcare-dashboard.css';

const REFRESH_LIMIT = 5;
const REFRESH_COOLDOWN_MS = 60_000;

type IconName = 'grid' | 'ticket' | 'check' | 'clock' | 'refresh' | 'link' | 'shield' | 'arrow' | 'info';

function Icon({ name, className = '' }: { name: IconName; className?: string }) {
  const paths: Record<IconName, React.ReactNode> = {
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </>
    ),
    ticket: (
      <>
        <path d="M4 5h16v5a2 2 0 0 0 0 4v5H4v-5a2 2 0 0 0 0-4V5Z" />
        <path d="M15 5v3m0 3v2m0 3v3" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 3 3 5-6" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7v5h-5M4 17v-5h5" />
        <path d="M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" />
      </>
    ),
    link: (
      <>
        <path
          d="m10 13 4-4m-6 6-1 1a4 4 0 0 1-6-6l4-4a4 4 0 0 1 6 0m2 2 1-1a4 4 0 0 1 6 6l-4 4a4 4 0 0 1-6 0"
          transform="translate(1 1)"
        />
      </>
    ),
    shield: (
      <>
        <path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" />
        <path d="m8 12 3 3 5-6" />
      </>
    ),
    arrow: <path d="m9 5 7 7-7 7" />,
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v6m0-10v.1" />
      </>
    ),
  };
  return (
    <svg
      className={`epd-icon ${className}`}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

const count = (value: number) => value.toLocaleString('ko-KR');
const dateFormat = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const timeFormat = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});
const date = (value: string | number) => dateFormat.format(new Date(value));
const time = (value: string | number) => timeFormat.format(new Date(value));

function couponStatus(coupon: EunpyeongCareCoupon) {
  if (coupon.isExpired || (coupon.expiresAt && new Date(coupon.expiresAt).getTime() <= Date.now())) {
    return {
      tone: 'expired',
      label: '등록 기간 만료',
      title: '쿠폰 등록 기간이 종료되었습니다.',
      description: '남은 수량이 있어도 등록 만료일이 지난 쿠폰은 등록할 수 없습니다.',
    };
  }
  if (!coupon.isUnlimited && coupon.remainingUses === 0) {
    return {
      tone: 'exhausted',
      label: '등록 수량 소진',
      title: '준비된 쿠폰 수량이 모두 소진되었습니다.',
      description: '전체 누적 등록 건수가 최대 등록 수량에 도달했습니다.',
    };
  }
  return {
    tone: 'active',
    label: '등록 진행 중',
    title: '은평케어 쿠폰 등록이 진행 중입니다.',
    description: '',
  };
}

function couponBenefit(coupon: EunpyeongCareCoupon) {
  if (coupon.type === 'discount') return `할인 값 ${count(coupon.value)}`;
  const units = { day: '일', month: '개월', year: '년' };
  return `${count(coupon.value)}${coupon.timeUnit ? units[coupon.timeUnit] : ''} 구독권`;
}

function CouponContent({ coupon }: { coupon: EunpyeongCareCoupon }) {
  const status = couponStatus(coupon);
  const percentage = coupon.isUnlimited ? null : Math.min(100, (coupon.uses / coupon.maxUses) * 100);
  const percentageLabel = percentage === null ? null : percentage.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
  const name = localizedCouponText(coupon.name, '은평케어 쿠폰');
  const description = localizedCouponText(coupon.description);

  return (
    <>
      <section className={`epd-status epd-status--${status.tone}`} aria-label="쿠폰 상태">
        <span className="epd-status__icon">
          <Icon name={status.tone === 'active' ? 'check' : 'info'} />
        </span>
        <div>
          <h2>{status.title}</h2>
          {status.description ? <p>{status.description}</p> : null}
        </div>
        <span className="epd-badge">
          <span />
          {status.label}
        </span>
      </section>

      <section className="epd-metrics" aria-label="쿠폰 주요 지표">
        <article className="epd-metric epd-metric--primary">
          <div className="epd-metric__label">
            누적 등록 건수
            <Icon name="check" />
          </div>
          <p className="epd-metric__value">
            {count(coupon.uses)}
            <span>건</span>
          </p>
          <p className="epd-metric__hint">발급 이후 전체 등록 건수</p>
        </article>
        <article className="epd-metric">
          <div className="epd-metric__label">
            남은 등록 수량
            <Icon name="ticket" />
          </div>
          <p className="epd-metric__value">
            {coupon.isUnlimited ? '무제한' : count(coupon.remainingUses ?? 0)}
            {!coupon.isUnlimited && <span>건</span>}
          </p>
          <p className="epd-metric__hint">
            {coupon.isUnlimited
              ? '등록 수량에 제한이 없습니다'
              : status.tone === 'expired'
                ? '수량 기준 잔여 · 등록 기간 만료'
                : '최대 등록 수량 − 누적 등록 건수'}
          </p>
        </article>
        <article className="epd-metric">
          <div className="epd-metric__label">
            최대 등록 수량
            <Icon name="grid" />
          </div>
          <p className="epd-metric__value">
            {coupon.isUnlimited ? '무제한' : count(coupon.maxUses)}
            {!coupon.isUnlimited && <span>건</span>}
          </p>
          <p className="epd-metric__hint">
            {coupon.isUnlimited ? '수량 제한 없이 운영' : '쿠폰에 설정된 전체 등록 한도'}
          </p>
        </article>
        <article className="epd-metric">
          <div className="epd-metric__label">
            등록 만료일
            <Icon name="clock" />
          </div>
          <p className={`epd-metric__value ${coupon.expiresAt ? 'epd-metric__value--date' : ''}`}>
            {coupon.expiresAt ? date(coupon.expiresAt) : '기한 없음'}
          </p>
          <p className="epd-metric__hint">
            {coupon.expiresAt ? `${time(coupon.expiresAt)}까지 · 한국 시간` : '등록 만료일이 설정되지 않았습니다'}
          </p>
        </article>
      </section>

      <div className="epd-detail-grid">
        <section className="epd-panel epd-progress-panel" aria-labelledby="epd-progress-title">
          <div className="epd-panel__heading">
            <div>
              <p className="epd-eyebrow">REGISTRATION OVERVIEW</p>
              <h2 id="epd-progress-title">쿠폰 등록 현황</h2>
            </div>
            <span className="epd-chip">누적 기준</span>
          </div>
          <div className="epd-progress-summary">
            <div>
              <p className="epd-progress-summary__label">{coupon.isUnlimited ? '누적 등록' : '등록률'}</p>
              <p className="epd-progress-summary__value">
                {coupon.isUnlimited ? count(coupon.uses) : percentageLabel}
                <span>{coupon.isUnlimited ? '건' : '%'}</span>
              </p>
            </div>
            <p>
              {coupon.isUnlimited ? '등록 수량 제한 없이' : `전체 ${count(coupon.maxUses)}건 중`}
              <br />
              <strong>{count(coupon.uses)}건 등록</strong>
            </p>
          </div>
          {percentage !== null ? (
            <div
              className="epd-progress"
              role="progressbar"
              aria-label="최대 등록 수량 대비 누적 등록률"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span style={{ width: `${percentage}%` }} />
            </div>
          ) : (
            <div className="epd-unlimited">
              <span aria-hidden="true">∞</span>
              <div>
                <strong>등록 수량 무제한</strong>
                <p>한도가 없어 등록률을 계산하지 않습니다.</p>
              </div>
            </div>
          )}
          <div className="epd-legend">
            <span>
              <i />
              등록 <strong>{count(coupon.uses)}건</strong>
            </span>
            <span>
              <i />
              잔여 <strong>{coupon.isUnlimited ? '무제한' : `${count(coupon.remainingUses ?? 0)}건`}</strong>
            </span>
          </div>
        </section>

        <section className="epd-panel epd-coupon-panel" aria-labelledby="epd-coupon-title">
          <div className="epd-panel__heading">
            <div>
              <p className="epd-eyebrow">COUPON INFORMATION</p>
              <h2 id="epd-coupon-title">쿠폰 정보</h2>
            </div>
            <Icon name="ticket" />
          </div>
          <div className="epd-coupon-name">
            <span className="epd-coupon-name__icon">
              <Icon name="ticket" />
            </span>
            <div>
              <h3>{name}</h3>
              <p>{couponBenefit(coupon)}</p>
            </div>
          </div>
          {description && <p className="epd-coupon-description">{description}</p>}
          <dl className="epd-facts">
            <div>
              <dt>쿠폰 코드</dt>
              <dd>
                <code>{coupon.code}</code>
              </dd>
            </div>
            <div>
              <dt>쿠폰 유형</dt>
              <dd>{coupon.type === 'subscription' ? '구독 쿠폰' : '할인 쿠폰'}</dd>
            </div>
            <div>
              <dt>{coupon.type === 'subscription' ? '제공 혜택' : '할인 값'}</dt>
              <dd>{coupon.type === 'subscription' ? couponBenefit(coupon) : count(coupon.value)}</dd>
            </div>
            <div>
              <dt>발급일</dt>
              <dd>
                {date(coupon.createdAt)} <span>{time(coupon.createdAt)}</span>
              </dd>
            </div>
            <div>
              <dt>등록 만료일</dt>
              <dd>
                {coupon.expiresAt ? (
                  <>
                    {date(coupon.expiresAt)} <span>{time(coupon.expiresAt)}</span>
                  </>
                ) : (
                  '기한 없음'
                )}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </>
  );
}

function EmptyState({ missing, onRetry, fetching }: { missing: boolean; onRetry: () => void; fetching: boolean }) {
  return (
    <section className="epd-empty epd-panel" role="status">
      <span className="epd-empty__icon">
        <Icon name={missing ? 'ticket' : 'info'} />
      </span>
      <p className="epd-eyebrow">{missing ? 'COUPON NOT ISSUED' : 'CONNECTION UNAVAILABLE'}</p>
      <h2>{missing ? '아직 발급된 은평케어 쿠폰이 없습니다' : '쿠폰 현황을 불러오지 못했습니다'}</h2>
      <p>
        {missing
          ? '쿠폰이 발급되면 이곳에서 등록 현황을 확인할 수 있습니다.'
          : '서버 연결 상태를 확인한 후 다시 시도해 주세요.'}
        <br />
        확인되지 않은 수치는 표시하지 않습니다.
      </p>
      <button type="button" className="epd-button epd-button--dark" onClick={onRetry} disabled={fetching}>
        <Icon name="refresh" className={fetching ? 'epd-spinning' : ''} />
        {fetching ? '확인 중' : '다시 확인'}
      </button>
    </section>
  );
}

export default function EunpyeongCareDashboardPage() {
  const { data, isPending, isFetching, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['public', 'eunpyeongcare-coupon'],
    queryFn: async ({ signal }) => {
      try {
        return await fetchEunpyeongCareCoupon(signal);
      } catch (error) {
        // A confirmed missing coupon replaces the cache so deleted coupons never reappear on later failures.
        if (error instanceof CouponDashboardError && error.kind === 'not-found') return null;
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);
  const [refreshLocked, setRefreshLocked] = useState(false);
  const refreshCountRef = useRef(0);
  const cooldownUntilRef = useRef(0);
  const missing = data === null && !isError;
  const coupon = data ?? undefined;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!refreshLocked) return;
    const remaining = Math.max(0, cooldownUntilRef.current - Date.now());
    const timer = window.setTimeout(() => {
      refreshCountRef.current = 0;
      cooldownUntilRef.current = 0;
      setRefreshLocked(false);
    }, remaining || REFRESH_COOLDOWN_MS);
    return () => window.clearTimeout(timer);
  }, [refreshLocked]);

  const showToast = (message: string) => {
    setToast((prev) => ({ id: (prev?.id ?? 0) + 1, message }));
  };

  const refresh = async () => {
    const now = Date.now();
    if (now < cooldownUntilRef.current) {
      showToast('1분 후 다시 시도해 주세요.');
      return;
    }

    refreshCountRef.current += 1;
    if (refreshCountRef.current >= REFRESH_LIMIT) {
      cooldownUntilRef.current = now + REFRESH_COOLDOWN_MS;
      setRefreshLocked(true);
      showToast('1분 후 다시 시도해 주세요.');
      return;
    }

    await refetch();
    showToast('새로고침을 완료했습니다.');
  };

  return (
    <div className="epd">
      <Helmet>
        <html lang="ko" />
        <title>마음 쉼터 쿠폰 현황 | SoundPill</title>
        <meta name="description" content="은평구 담당자를 위한 은평케어 쿠폰 등록 현황 대시보드" />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="theme-color" content="#f8f8f8" />
        <link rel="canonical" href="https://soundpill.app/eunpyeongcare/dashboard" />
      </Helmet>
      <a className="epd-skip" href="#epd-main">
        본문으로 건너뛰기
      </a>
      <div className="epd-ambient" aria-hidden="true" />
      <div className="epd-workspace">
        <header className="epd-hero">
          <div className="epd-hero__bar">
            <div className="epd-brand">
              SoundPill
            </div>
            <div className="epd-hero__meta">
              <span className="epd-readonly">
                <Icon name="shield" />
                조회 전용
              </span>
            </div>
          </div>
          <div className="epd-page-heading">
            <div>
              <p className="epd-eyebrow">불광보건지소</p>
              <h1>
                마음 쉼터 쿠폰 현황<span className="epd-heading-dot">.</span>
              </h1>
            </div>
          </div>
        </header>
        <main id="epd-main" className="epd-main">
          <div className="epd-update-bar">
            <span>
              <i className={isError ? 'epd-update-dot epd-update-dot--error' : 'epd-update-dot'} />
              {dataUpdatedAt ? (
                <>
                  마지막 업데이트{' '}
                  <strong>
                    {date(dataUpdatedAt)} {time(dataUpdatedAt)}
                  </strong>
                  <span className="epd-update-timezone"> KST</span>
                </>
              ) : isPending ? (
                '쿠폰 현황을 확인하고 있습니다'
              ) : (
                '업데이트 대기 중'
              )}
            </span>
            <div>
              <button
                type="button"
                className="epd-refresh"
                onClick={() => void refresh()}
                disabled={isFetching || refreshLocked}
              >
                <Icon name="refresh" className={isFetching ? 'epd-spinning' : ''} />
                {isFetching ? '업데이트 중' : refreshLocked ? '잠시 후 다시' : '새로고침'}
              </button>
            </div>
          </div>

          {isError && coupon && (
            <div className="epd-stale" role="alert">
              <Icon name="info" />
              <p>최신 정보를 가져오지 못해 마지막으로 확인한 현황을 표시합니다. 위 업데이트 시각을 확인해 주세요.</p>
            </div>
          )}
          {isPending ? (
            <div className="epd-loading" role="status" aria-label="쿠폰 현황 불러오는 중">
              <div className="epd-skeleton epd-skeleton--status" />
              <div className="epd-metrics">
                {[0, 1, 2, 3].map((key) => (
                  <div className="epd-skeleton epd-skeleton--metric" key={key} />
                ))}
              </div>
              <div className="epd-skeleton epd-skeleton--panel" />
              <span className="epd-sr-only">쿠폰 현황을 불러오고 있습니다.</span>
            </div>
          ) : coupon ? (
            <CouponContent coupon={coupon} />
          ) : (
            <EmptyState missing={missing} onRetry={() => void refresh()} fetching={isFetching} />
          )}

          <footer className="epd-footer">
            <span>
              © SoundPill <i />
              은평구 불광보건지소 대시보드
            </span>
            <span>EUNPYEONGCARE</span>
          </footer>
        </main>
      </div>
      {toast ? (
        <div key={toast.id} className="epd-toast" role="status" aria-live="polite">
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}
